import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useIsPublicRoute } from "../lib/publicRoutes";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { X, Cookie, Settings, ChevronDown, ChevronUp } from "lucide-react";

const COOKIE_CONSENT_KEY = "cookie_consent";
const CONSENT_EXPIRY_DAYS = 180; // 6 months as per GDPR guidelines

export function CookieBanner() {
	const isPublicRoute = useIsPublicRoute();
	const { language } = useLanguage();
	const [showBanner, setShowBanner] = useState(false);
	const [showDetails, setShowDetails] = useState(false);
	const [preferences, setPreferences] = useState({
		necessary: true, // Always true, cannot be disabled
		functional: false,
		analytics: false,
		marketing: false,
	});

	useEffect(() => {
		checkConsent();
	}, []);

	const checkConsent = () => {
		const consent = localStorage.getItem(COOKIE_CONSENT_KEY);

		if (!consent) {
			setShowBanner(true);
			return;
		}

		try {
			const { timestamp, preferences: savedPrefs } = JSON.parse(consent);
			const consentDate = new Date(timestamp);
			const now = new Date();
			const daysDiff = (now - consentDate) / (1000 * 60 * 60 * 24);

			// Re-prompt after 6 months
			if (daysDiff > CONSENT_EXPIRY_DAYS) {
				setShowBanner(true);
				return;
			}

			setPreferences(savedPrefs);
		} catch (e) {
			setShowBanner(true);
		}
	};

	const saveConsent = (prefs) => {
		const consent = {
			timestamp: new Date().toISOString(),
			preferences: prefs,
		};
		localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
		setPreferences(prefs);
		setShowDetails(false);
		setShowBanner(false);
		window.dispatchEvent(new Event("cookie-consent-changed"));

		// Apply preferences (e.g., load/block analytics scripts)
		applyPreferences(prefs);
	};

	const applyPreferences = (prefs) => {
		// Here you would enable/disable third-party scripts based on preferences
		// For now, we just log the preferences
		console.log("Cookie preferences applied:", prefs);

		// Example: If analytics is enabled and you have GA
		// if (prefs.analytics) {
		//   window.gtag('consent', 'update', { analytics_storage: 'granted' });
		// }
	};

	const acceptAll = () => {
		const allAccepted = {
			necessary: true,
			functional: true,
			analytics: true,
			marketing: true,
		};
		saveConsent(allAccepted);
	};

	const acceptNecessaryOnly = () => {
		const necessaryOnly = {
			necessary: true,
			functional: false,
			analytics: false,
			marketing: false,
		};
		saveConsent(necessaryOnly);
	};

	const saveCustomPreferences = () => {
		saveConsent(preferences);
	};

	// Function to reopen banner (called from footer)
	const reopenBanner = () => {
		const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
		if (consent) {
			try {
				const { preferences: savedPrefs } = JSON.parse(consent);
				setPreferences(savedPrefs);
			} catch (e) {}
		}
		setShowDetails(false);
		setShowBanner(true);
	};

	// Expose reopen function globally
	useEffect(() => {
		window.openCookieSettings = reopenBanner;
		return () => {
			delete window.openCookieSettings;
		};
	}, []);

	useEffect(() => {
		if (!showBanner) return;

		// Lock scroll on the main content area (Layout scroll container)
		const main = document.getElementById("main-scroll-area");
		const originalOverflow = main ? main.style.overflow : null;
		if (main) main.style.overflow = "hidden";
		// Also lock body for pages without Layout (login, public pages)
		const originalBodyOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			if (main) main.style.overflow = originalOverflow || "";
			document.body.style.overflow = originalBodyOverflow;
		};
	}, [showBanner]);

	if (!showBanner) return null;

	const text =
		language === "it"
			? {
					title: "Informativa Cookie",
					description:
						"Utilizziamo cookie tecnici necessari per il funzionamento del sito. Con il tuo consenso, possiamo usare cookie funzionali e strumenti analitici sulla landing pubblica.",
					necessary: "Cookie Necessari",
					necessaryDesc:
						"Essenziali per il funzionamento del sito. Non possono essere disattivati.",
					functional: "Cookie Funzionali",
					functionalDesc:
						"Permettono funzionalità avanzate come la memorizzazione delle preferenze.",
					analytics: "Cookie Analitici",
					analyticsDesc:
						"Metricool misura gli accessi alla landing pubblica. Richiede il tuo consenso.",
					marketing: "Cookie di Marketing",
					marketingDesc:
						"Utilizzati per mostrarti pubblicità pertinenti (attualmente non attivi).",
					acceptAll: "Accetta Tutti",
					acceptNecessary: "Solo Necessari",
					customize: "Personalizza",
					savePreferences: "Salva Preferenze",
					moreInfo: "Maggiori informazioni",
					privacyPolicy: "Informativa Privacy",
					cookiePolicy: "Cookie Policy",
				}
			: {
					title: "Cookie Notice",
					description:
						"We use necessary technical cookies for the site to function. With your consent, we may also use functional cookies and analytics on the public landing page.",
					necessary: "Necessary Cookies",
					necessaryDesc: "Essential for the site to work. Cannot be disabled.",
					functional: "Functional Cookies",
					functionalDesc: "Enable advanced features like preference storage.",
					analytics: "Analytics Cookies",
					analyticsDesc:
						"Metricool measures visits to the public landing page and requires your consent.",
					marketing: "Marketing Cookies",
					marketingDesc:
						"Used to show you relevant ads (currently not active).",
					acceptAll: "Accept All",
					acceptNecessary: "Necessary Only",
					customize: "Customize",
					savePreferences: "Save Preferences",
					moreInfo: "More information",
					privacyPolicy: "Privacy Policy",
					cookiePolicy: "Cookie Policy",
				};

	/* Le pagine pubbliche (landing, login, policy, invito) restano scure
	   anche con il tema chiaro attivo: il banner e' portato su document.body,
	   quindi fuori dal loro contenitore, e senza questo si vedrebbe una card
	   chiara sopra una pagina nera. */
	const scopeClass = isPublicRoute ? "theme-dark-scope" : "";

	const portalContent = (
		<div className={scopeClass}>
			{/* Backdrop: solo dentro l'app. Sulle pagine pubbliche (landing,
			    login, policy) oscurerebbe l'intera pagina al primo arrivo. */}
			{!isPublicRoute && (
				<div className="fixed inset-0 z-[10020] bg-black/55" aria-hidden="true" />
			)}
			{/* Banner pinned to bottom */}
			<div
				data-testid="cookie-banner"
				className="fixed left-0 right-0 z-[10021] px-3 pb-3 sm:px-4 sm:pb-4 flex justify-center"
				style={{ bottom: "max(0.5rem, env(safe-area-inset-bottom, 0px))" }}
			>
				<Card className="w-full max-w-2xl bg-card border-border rounded-sm shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[78vh] overflow-y-auto">
					<div className="p-5 sm:p-6">
						{/* Header */}
						<div className="flex items-start justify-between mb-4">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 bg-volt/10 rounded-sm flex items-center justify-center">
									<Cookie className="w-5 h-5 text-volt" />
								</div>
								<h2 className="font-heading text-xl">{text.title}</h2>
							</div>
							<Button
								variant="ghost"
								size="icon"
								className="rounded-sm"
								onClick={acceptNecessaryOnly}
								aria-label="Close"
							>
								<X className="w-5 h-5" />
							</Button>
						</div>

						{/* Description */}
						<p className="text-sm text-muted-foreground mb-4">
							{text.description}
						</p>

						{/* Details Toggle */}
						<Button
							variant="ghost"
							size="sm"
							className="mb-4 text-volt hover:text-volt/80"
							onClick={() => setShowDetails(!showDetails)}
						>
							<Settings className="w-4 h-4 mr-2" />
							{text.customize}
							{showDetails ? (
								<ChevronUp className="w-4 h-4 ml-2" />
							) : (
								<ChevronDown className="w-4 h-4 ml-2" />
							)}
						</Button>

						{/* Cookie Categories (expandable) */}
						{showDetails && (
							<div className="space-y-4 mb-6 p-4 bg-secondary rounded-sm">
								{/* Necessary - Always on */}
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<Label className="font-medium">{text.necessary}</Label>
										<p className="text-xs text-muted-foreground">
											{text.necessaryDesc}
										</p>
									</div>
									<Switch checked={true} disabled className="ml-4" />
								</div>

								{/* Functional */}
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<Label className="font-medium">{text.functional}</Label>
										<p className="text-xs text-muted-foreground">
											{text.functionalDesc}
										</p>
									</div>
									<Switch
										checked={preferences.functional}
										onCheckedChange={(v) =>
											setPreferences({ ...preferences, functional: v })
										}
										className="ml-4"
									/>
								</div>

								{/* Analytics */}
								<div className="flex items-start justify-between">
									<div className="flex-1">
										<Label className="font-medium">{text.analytics}</Label>
										<p className="text-xs text-muted-foreground">
											{text.analyticsDesc}
										</p>
									</div>
									<Switch
										checked={preferences.analytics}
										onCheckedChange={(v) =>
											setPreferences({ ...preferences, analytics: v })
										}
										className="ml-4"
									/>
								</div>

								{/* Marketing */}
								<div className="flex items-start justify-between opacity-60">
									<div className="flex-1">
										<Label className="font-medium">{text.marketing}</Label>
										<p className="text-xs text-muted-foreground">
											{text.marketingDesc}
										</p>
									</div>
									<Switch
										checked={preferences.marketing}
										onCheckedChange={(v) =>
											setPreferences({ ...preferences, marketing: v })
										}
										className="ml-4"
										disabled
									/>
								</div>
							</div>
						)}

						{/* Action Buttons */}
						<div className="flex flex-col sm:flex-row gap-3">
							{showDetails ? (
								<Button
									onClick={saveCustomPreferences}
									className="flex-1 bg-volt hover:bg-volt/90 rounded-sm"
								>
									{text.savePreferences}
								</Button>
							) : (
								<>
									<Button
										onClick={acceptNecessaryOnly}
										variant="outline"
										className="flex-1 rounded-sm"
									>
										{text.acceptNecessary}
									</Button>
									<Button
										onClick={acceptAll}
										className="flex-1 bg-volt hover:bg-volt/90 rounded-sm"
									>
										{text.acceptAll}
									</Button>
								</>
							)}
						</div>

						{/* Links */}
						<div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
							<Link to="/privacy-policy" className="hover:text-volt underline">
								{text.privacyPolicy}
							</Link>
							<span>•</span>
							<Link to="/cookie-policy" className="hover:text-volt underline">
								{text.cookiePolicy}
							</Link>
						</div>
					</div>
				</Card>
			</div>
		</div>
	);

	return createPortal(portalContent, document.body);
}
